// Breadcrumb.tsx
import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useCustomTheme } from '../../../contexts/ThemeContext';

export interface BreadcrumbItem {
    label: string;
    path?: string; // if undefined, item is not clickable
}

interface BreadcrumbProps {
    section: BreadcrumbItem; // e.g. { label: 'Blogs', path: '/blogs' }
    items?: BreadcrumbItem[]; // additional crumbs after section
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ section, items = [] }) => {
    const navigate = useNavigate();
    const { theme } = useCustomTheme();

    const home: BreadcrumbItem = { label: 'Home', path: '/' };

    // Build full crumb list: Home > Section > ...items
    const crumbs: BreadcrumbItem[] = [home, section, ...items];

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
                mb: 2,
            }}
        >
            {crumbs.map((crumb, index) => {
                const isLast = index === crumbs.length - 1;
                const isClickable = !isLast && !!crumb.path;

                return (
                    <React.Fragment key={index}>
                        <Typography
                            variant="body2"
                            onClick={() => {
                                if (isClickable && crumb.path) {
                                    navigate(crumb.path);
                                }
                            }}
                            sx={{
                                cursor: isClickable ? 'pointer' : 'default',
                                color: isLast
                                    ? theme.palette.text.primary
                                    : theme.palette.text.secondary,
                                fontWeight: isLast ? 700 : 400,
                                '&:hover': isClickable
                                    ? {
                                          color: theme.palette.primary.main,
                                          textDecoration: 'underline',
                                      }
                                    : {},
                                transition: 'color 0.15s ease',
                            }}
                        >
                            {crumb.label}
                        </Typography>
                        {!isLast && (
                            <NavigateNextIcon
                                sx={{
                                    fontSize: 16,
                                    color: theme.palette.text.disabled,
                                }}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </Box>
    );
};

export default Breadcrumb;